import React, { useState } from "react";
import { useMutation, useApolloClient } from "@apollo/client";
import * as Sentry from "@sentry/react";

import { GET_DOWNLOAD_ICON } from "./mutations";
import { BASIC_COPY_SIZE, PRO_COPY_SIZE } from "icons/helpers";
import {
    trackDownload,
    formatValuesForApollo,
    getIconFilename,
    browserCanCopy,
    hasRateLimitError
} from "./helpers";
import { trackIconDownload } from "core/trackEvent";

const useIconDownload = ({
    icon,
    downloadType,
    downloadSize,
    editorValues = {},
    client // optional - for legacy pages
}) => {
    const iconId = icon ? icon.id : null;

    const DOWNLOAD_STARTED = 1;
    const DOWNLOAD_COMPLETED = 2;
    const DOWNLOAD_FAILED = 3;
    const [status, setStatus] = useState();

    const variables = formatValuesForApollo({
        iconId,
        downloadType,
        downloadSize,
        ...editorValues
    });
    const [fetchDownload] = useMutation(GET_DOWNLOAD_ICON, {
        variables,
        client
    });

    const startDownload = async () => {
        setStatus(DOWNLOAD_STARTED);
        try {
            const { data } = await fetchDownload();
            if (!!data && data.downloadIcon) {
                const { ok, base64Stream } = data.downloadIcon;
                if (ok) {
                    const contents = `data:application/octet-stream;base64,${base64Stream}`;
                    const filename = getIconFilename({
                        icon,
                        type: downloadType,
                        color: variables.foregroundColor
                    });

                    // The only way to download a base64 image with a customized filename
                    // is to use an anchor-tag with the "download" attribute.
                    const a = document.createElement("a");
                    a.setAttribute("href", contents);
                    a.setAttribute("download", filename);
                    a.setAttribute("target", "_blank");
                    a.click();
                    setStatus(DOWNLOAD_COMPLETED);
                    trackDownload({ downloadType, iconId });
                    trackIconDownload();
                    return;
                }
            }
        } catch (error) {
            if (error && hasRateLimitError(error.graphQLErrors)) {
                Sentry.captureException(
                    `Rate limit exceeded while downloading`
                );
            } else {
                Sentry.captureException(
                    `Failed to download icon. Error: ${error}`
                );
            }
        }
        setStatus(DOWNLOAD_FAILED);
    };

    return {
        startDownload,
        isStarted: status === DOWNLOAD_STARTED,
        isCompleted: status === DOWNLOAD_COMPLETED,
        hasFailed: status === DOWNLOAD_FAILED
    };
};

export default useIconDownload;

export const useIconCopy = () => {
    // keep track of what stage we're in
    const COPY_STARTED = 1;
    const COPY_COMPLETED = 2;
    const COPY_FAILED = 3;
    const [status, setStatus] = useState();

    // Safari's security policy doesn't play nicely with useMutation. We have to query directly.
    const client = useApolloClient();

    // `editorValues` are optional; if absent, default values are applied.
    const copy = async ({
        userHasSubscription,
        iconId,
        editorValues = {},
        iconIsPublicDomain = false
    }) => {
        setStatus(COPY_STARTED);
        const downloadType = "PNG";
        const downloadSize =
            userHasSubscription || iconIsPublicDomain
                ? PRO_COPY_SIZE
                : BASIC_COPY_SIZE;
        const variables = formatValuesForApollo({
            iconId,
            downloadType,
            downloadSize,
            ...editorValues
        });
        const data = await client
            .mutate({
                mutation: GET_DOWNLOAD_ICON,
                variables
            })
            .then(response => response.data)
            .catch(error => {
                if (error && hasRateLimitError(error.graphQLErrors)) {
                    Sentry.captureException(
                        `Rate limit exceeded while copying`
                    );
                } else {
                    Sentry.captureException(
                        `Download mutation failed. Error: ${error}`
                    );
                }
                return null;
            });
        if (data && data.downloadIcon) {
            const { ok, errors, base64Stream } = data.downloadIcon;
            if (ok && !errors) {
                const contents = `data:image/png;base64,${base64Stream}`;
                try {
                    const res = await fetch(contents);
                    const blob = await res.blob();
                    setStatus(COPY_COMPLETED);
                    trackDownload({ downloadType, iconId, type: "copy" });
                    trackIconDownload();
                    return blob;
                } catch (e) {
                    Sentry.captureException(
                        `Failed to download icon for copying. Error: ${e}`
                    );
                }
            }
        }
        setStatus(COPY_FAILED);
        return null;
    };

    return {
        copy,
        browserCanCopy,
        isStarted: status === COPY_STARTED,
        isCompleted: status === COPY_COMPLETED,
        hasFailed: status === COPY_FAILED
    };
};
